import React, { useEffect, useState } from "react";
import { TbUserEdit } from "react-icons/tb";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { GLOBAL_ORGANIZATION, SIDEBAR_TEXTS } from "@/utils/constants/appConstants/commonConstants";
import { isFeaturePermitted, FEATURES_LOADED_EVENT } from "@/utils/functions/rbac";
import { getCurrentOrganization } from "@/utils/functions/OrganizationFunctions";
import { useAppShellOptional } from "@/components/layouts/app-shell-context";

type SidebarNavItemProps = {
    to: string;
    active: boolean;
    label: string;
    labelId: string;
    icon: React.ReactNode;
};

function SidebarNavItem({ to, active, label, labelId, icon }: SidebarNavItemProps) {
    const shell = useAppShellOptional();
    const collapsed = shell?.sidebarCollapsed ?? false;

    return (
        <li className="gs-sidebar-item">
            <Link
                to={to}
                className={cn("gs-sidebar-link", active && "gs-sidebar-link--active", collapsed && "gs-sidebar-link--collapsed")}
                title={collapsed ? label : undefined}
            >
                {icon}
                <span
                    id={labelId}
                    className={cn("gs-sidebar-label", collapsed && "gs-sidebar-label--collapsed")}
                >
                    {label}
                </span>
            </Link>
        </li>
    );
}

function SideBarIcon({ id, name }: { id: string; name: string }) {
    return (
        <span className="gs-sidebar-icon">
            <em id={id} className={`icon ni ni-${name}`} />
        </span>
    );
}

function SideBar() {
    const location = useLocation();
    const pathname = location.pathname;
    const shell = useAppShellOptional();
    const collapsed = shell?.sidebarCollapsed ?? false;

    const [isViewUserPermission, setIsViewUserPermission] = useState(false);
    const [isViewRolesPermitted, setIsViewRolesPermitted] = useState(false);
    const [isViewGroupsPermitted, setIsViewGroupsPermitted] = useState(false);
    const [isViewEnvelopesPermitted, setIsViewEnvelopesPermission] = useState(false);
    const [isViewSettingsPermitted, setIsViewSettingsPermission] = useState(false);
    const [isViewBrandingPermitted, setIsViewBrandingPermission] = useState(false);
    const [isViewTemplatesPermitted, setIsViewTemplatesPermission] = useState(false);
    const [isViewFormsPermitted, setIsViewFormsPermission] = useState(false);
    const [isViewTagsPermitted, setIsViewTagsPermitted] = useState(false);
    const [isViewLLMCredentialPermitted, setIsViewLLMCredentialPermission] = useState(false);
    const [isViewReportedUsersPermitted, setIsViewReportedUsersPermission] = useState(false);

    useEffect(() => {
        const refreshPermissions = () => {
            setIsViewUserPermission(isFeaturePermitted("AdminPortalViewUser"));
            setIsViewRolesPermitted(isFeaturePermitted("AdminPortalViewRole"));
            setIsViewGroupsPermitted(isFeaturePermitted("AdminPortalViewGroup"));
            setIsViewEnvelopesPermission(isFeaturePermitted("AdminPortalViewEnvelope"));
            setIsViewSettingsPermission(isFeaturePermitted("AdminPortalViewOrganizationSetting"));
            setIsViewBrandingPermission(isFeaturePermitted("AdminPortalViewBrandingSetting"));
            setIsViewTemplatesPermission(isFeaturePermitted("AdminPortalViewTemplate"));
            setIsViewFormsPermission(isFeaturePermitted("AdminPortalViewForm"));
            setIsViewLLMCredentialPermission(isFeaturePermitted("AdminPortalViewLLMCredential"));
            setIsViewReportedUsersPermission(isFeaturePermitted("AdminPortalViewReportedUsers"));
            setIsViewTagsPermitted(isFeaturePermitted("AdminPortalViewTags"));
        };

        refreshPermissions();
        window.addEventListener(FEATURES_LOADED_EVENT, refreshPermissions);
        return () => window.removeEventListener(FEATURES_LOADED_EVENT, refreshPermissions);
    }, []);

    return (
        <aside
            className={cn(
                "gs-app-sidebar gs-desktop-sidebar",
                collapsed && "gs-app-sidebar--collapsed"
            )}
            data-content="sidebarMenu"
            aria-label="Main navigation"
        >
            <nav className="gs-app-sidebar-inner">
                <ul className="gs-sidebar-nav">
                    <SidebarNavItem
                        to="/dashboard"
                        active={pathname === "/" || pathname.startsWith("/dashboard")}
                        label={SIDEBAR_TEXTS.Dashboard}
                        labelId="sidebar-dashboard-option"
                        icon={<SideBarIcon id="sidebar-dashboard-icon" name="dashboard" />}
                    />
                    {isViewUserPermission && (
                        <SidebarNavItem
                            to="/users"
                            active={pathname.startsWith("/user")}
                            label={SIDEBAR_TEXTS.Users}
                            labelId="sidebar-user-option"
                            icon={<SideBarIcon id="sidebar-user-icon" name="user" />}
                        />
                    )}
                    {isViewUserPermission && (
                        <SidebarNavItem
                            to="/admins"
                            active={pathname.startsWith("/admin")}
                            label={SIDEBAR_TEXTS.Admins}
                            labelId="sidebar-admin-option"
                            icon={<SideBarIcon id="sidebar-admin-icon" name="account-setting" />}
                        />
                    )}
                    {isViewReportedUsersPermitted &&
                        getCurrentOrganization().name === GLOBAL_ORGANIZATION && (
                            <SidebarNavItem
                                to="/reported-users"
                                active={pathname.startsWith("/reported-users")}
                                label={SIDEBAR_TEXTS.ReportedUsers}
                                labelId="sidebar-reported-users-option"
                                icon={<SideBarIcon id="sidebar-reported-users-icon" name="user" />}
                            />
                        )}
                    {isViewRolesPermitted && (
                        <SidebarNavItem
                            to="/roles"
                            active={pathname.startsWith("/role")}
                            label={SIDEBAR_TEXTS.Roles}
                            labelId="sidebar-role-option"
                            icon={
                                <span className="gs-sidebar-icon">
                                    <TbUserEdit id="sidebar-role-icon" aria-hidden />
                                </span>
                            }
                        />
                    )}
                    {isViewGroupsPermitted && (
                        <SidebarNavItem
                            to="/groups"
                            active={pathname.startsWith("/group")}
                            label={SIDEBAR_TEXTS.Groups}
                            labelId="sidebar-group-option"
                            icon={<SideBarIcon id="sidebar-group-icon" name="users" />}
                        />
                    )}
                    {isViewEnvelopesPermitted && (
                        <SidebarNavItem
                            to="/envelopes"
                            active={pathname.startsWith("/envelope")}
                            label={SIDEBAR_TEXTS.Envelopes}
                            labelId="sidebar-envelope-option"
                            icon={<SideBarIcon id="sidebar-envelope-icon" name="file" />}
                        />
                    )}
                    {isViewTemplatesPermitted && (
                        <SidebarNavItem
                            to="/templates"
                            active={pathname.startsWith("/template")}
                            label={SIDEBAR_TEXTS.Templates}
                            labelId="sidebar-envelope-option"
                            icon={<SideBarIcon id="sidebar-envelope-icon" name="template" />}
                        />
                    )}
                    {isViewFormsPermitted && (
                        <SidebarNavItem
                            to="/forms"
                            active={pathname.startsWith("/form")}
                            label={SIDEBAR_TEXTS.Forms}
                            labelId="sidebar-envelope-option"
                            icon={<SideBarIcon id="sidebar-envelope-icon" name="notes-alt" />}
                        />
                    )}
                    {isViewTagsPermitted && (
                        <SidebarNavItem
                            to="/tags"
                            active={pathname.startsWith("/tags")}
                            label={SIDEBAR_TEXTS.Tags}
                            labelId="sidebar-tags-option"
                            icon={<SideBarIcon id="sidebar-tags-icon" name="label" />}
                        />
                    )}
                    {isViewSettingsPermitted && (
                        <SidebarNavItem
                            to="/settings"
                            active={pathname.startsWith("/settings")}
                            label={SIDEBAR_TEXTS.Settings}
                            labelId="sidebar-setting-option"
                            icon={<SideBarIcon id="sidebar-setting-icon" name="setting" />}
                        />
                    )}
                    {isViewBrandingPermitted && (
                        <SidebarNavItem
                            to="/brands"
                            active={pathname.startsWith("/brand")}
                            label={SIDEBAR_TEXTS.Brand}
                            labelId="sidebar-brand-option"
                            icon={<SideBarIcon id="sidebar-brand-icon" name="star" />}
                        />
                    )}
                    {isViewBrandingPermitted && (
                        <SidebarNavItem
                            to="/logs"
                            active={pathname.startsWith("/logs")}
                            label={SIDEBAR_TEXTS.Logs}
                            labelId="sidebar-logs-option"
                            icon={<SideBarIcon id="sidebar-logs-icon" name="file-docs" />}
                        />
                    )}
                    {isViewLLMCredentialPermitted && (
                        <SidebarNavItem
                            to="/manage-ai"
                            active={pathname.startsWith("/manage-ai")}
                            label={SIDEBAR_TEXTS.ManageAI}
                            labelId="sidebar-ai-option"
                            icon={<SideBarIcon id="sidebar-ai-icon" name="cpu" />}
                        />
                    )}
                </ul>
            </nav>
        </aside>
    );
}

export default SideBar;
